import Card from './Card'
import {createUseStyles} from 'react-jss'
import {Colors} from '../constants/colors'
import {formatDateString, formatDateTimeString} from '../utils/dateHelper'
import React, {useMemo, useState} from 'react'
import {CanonicalPayModel, EmploymentContractModel} from '../redux/types'
import DetailsCard from './DetailsCard'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faCircle} from '@fortawesome/free-solid-svg-icons'
import {formatMoney} from '../utils/moneyHelper'
import {
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table'

interface Props {
  employmentContracts: EmploymentContractModel[]
}

const EmploymentContractList = ({employmentContracts}: Props) => {
  const styles = useStyles()
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const notSpecified = 'Not specified'
  const formatSpecifiedMoney = (money?: {amount: number, currencyCode: string}) => money ? formatMoney(money) : notSpecified

  const columns = useMemo<ColumnDef<EmploymentContractModel>[]>(() => [
    {
      id: 'state',
      header: 'State',
      cell: ({row}) => (
        row.original.terminated ? (
          <FontAwesomeIcon icon={faCircle} className={styles.terminatedIcon} />
        ) : (
          <FontAwesomeIcon icon={faCircle} className={styles.activeIcon} />
        )
      ),
    },
    {
      id: 'startDate',
      header: 'Start date',
      cell: ({row}) => <span>{formatDateString(row.original.startDateString)}</span>,
    },
    {
      id: 'endDate',
      header: 'End date',
      cell: ({row}) => <span>{row.original.endDateString ? formatDateString(row.original.endDateString) : notSpecified}</span>,
    },
    {
      id: 'currentSalary',
      header: 'Current salary',
      cell: ({row}) => (
        <span>{formatSpecifiedMoney(row.original.currentSalary)}</span>
      ),
    },
    {
      id: 'currentWage',
      header: 'Current wage',
      cell: ({row}) => (
        <span>{formatSpecifiedMoney(row.original.currentHourlyWage)}</span>
      ),
    },
  ], [])

  const table = useReactTable({
    data: employmentContracts ?? [],
    columns,
    state: {expanded},
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
  })

  const renderCanonicalPayHistory = (pays: CanonicalPayModel[]) => {
    if (!pays || pays.length === 0) {
      return (
        <DetailsCard>
          <p>No pay history available for this contract.</p>
        </DetailsCard>
      );
    }

    return (
      <div className={styles.payHistoryList}>
        {pays.map((pay, index) => (
          <div className={styles.payHistoryItem} key={`canonical-pay-${index}`}>
            <p><span>{formatDateTimeString(pay.startDateString)} - {!!pay.endDateString ? formatDateTimeString(pay.endDateString) : 'present'}</span></p>
            <p>Gross hourly wage: <span>{formatSpecifiedMoney(pay.grossHourlyWage)}</span></p>
            <p>Gross salary: <span>{formatSpecifiedMoney(pay.grossSalary)}</span></p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id} className={styles.headerCell}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
          </thead>
          <tbody>
          {table.getRowModel().rows.map(row => (
            <React.Fragment key={row.id}>
              <tr
                className={styles.bodyRow}
                onClick={() => row.toggleExpanded()}
                tabIndex={0}
              >
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className={styles.bodyCell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
              {row.getIsExpanded() && (
                <tr>
                  <td className={styles.expandedCell} colSpan={Math.ceil(row.getVisibleCells().length)}>
                    <div className={styles.detailsContainer}>
                      <Card className={`${styles.detailsCard} card`}>
                        <h6>Details</h6>
                        <p>Terminated: <span>{row.original.terminated ? 'yes' : 'no'}</span></p>
                        <p>External Id: <span>{row.original.externalId || notSpecified}</span></p>
                        <p>Report Id: <span>{row.original.reportId || notSpecified}</span></p>
                        <p>Description: <span>{row.original.description || notSpecified}</span></p>
                        <p>Cost center: <span>{row.original.costCenter || notSpecified}</span></p>
                        <p>Pay schedule: <span>{row.original.payScheduleIdentifier}</span></p>
                        <p>Pay calculation type: <span>{row.original.payCalculationType}</span></p>
                        <p>Start date: <span>{row.original.startDateString}</span></p>
                        <p>End date: <span>{row.original.endDateString || notSpecified}</span></p>
                        <p>Handle vacation pay: <span>{row.original.handleVacationPay ? 'yes' : 'no'}</span></p>
                        <p>Vacation pay percent: <span>{row.original.vacationPayPercent || notSpecified}</span></p>
                      </Card>
                      <Card className={`${styles.detailsCard} card`}>
                        <h6>Metadata</h6>
                        <p>Collective agreement: <span>{row.original.collectiveAgreement || notSpecified}</span></p>
                        <p>Position: <span>{row.original.position || notSpecified}</span></p>
                        <p>Weekly work hours: <span>{row.original.weeklyWorkHours ?? notSpecified}</span></p>
                        <p>Monthly work hours: <span>{row.original.monthlyWorkHours ?? notSpecified}</span></p>
                        <p>Employment rate percent: <span>{row.original.employmentRatePercent ?? notSpecified}</span></p>
                        <p>Monthly salary: <span>{formatSpecifiedMoney(row.original.monthlySalary)}</span></p>
                        <p>Hourly wage: <span>{formatSpecifiedMoney(row.original.hourlyWage)}</span></p>
                      </Card>
                      <Card className={`${styles.detailsCard} card`}>
                        <h6>Pay</h6>
                        {renderCanonicalPayHistory(row.original.canonicalPays)}
                      </Card>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default EmploymentContractList

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  tableContainer: {
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 0px',
    fontSize: '14px',
  },
  headerCell: {
    padding: '6px 12px',
    textAlign: 'left',
    fontFamily: 'Aestetico-Bold'
  },
  bodyRow: {
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    },
  },
  bodyCell: {
    padding: '2px 12px',
    textAlign: 'left',
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  expandedCell: {
    paddingTop: 2,
    paddingBottom: 22,
    paddingLeft: 22,
    paddingRight: 2,
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  detailsCard: {
    flex: '1 1 360px',
    minWidth: 280,
    padding: 8,
    borderRadius: 8,
    backgroundColor: Colors.grayCard
  },
  payHistoryList: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  payHistoryItem: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 300px',
    padding: 8,
    backgroundColor: Colors.darkGray4,
    borderRadius: 8
  },
  terminatedIcon: {
    color: Colors.red,
    fontSize: 10,
  },
  activeIcon: {
    color: Colors.green,
    fontSize: 10,
  }
})
